import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Trash2, User, Image as ImageIcon, Plus, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api, getPropertyCoverImageUrl, getUploadsPublicUrl, PROPERTY_IMAGE_PLACEHOLDER } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { Modal } from '../../components/ui/Modal';
import { PropertySelect } from '../../components/ui/PropertySelect';
import { ChangeLogEntry, Property } from '../../types';
import { Toast } from '../../components/ui/Toast';
import { formatDateLong, formatDateTime } from '../../utils/date';
import { isMaintenanceRoleName } from '../../utils/investorRole';
import { MaintenanceReportWorkTimeline } from '../../components/maintenance/MaintenanceReportWorkTimeline';
import { MaintenanceTimeline } from '../../components/maintenance/MaintenanceTimeline';
import { HistoryTable } from '../../components/history/HistoryTable';

type ReportStatus = 'pending' | 'in_progress' | 'reviewed' | 'resolved';

interface MaintenanceReportDetailData {
  id: string;
  propertyId: string;
  unitId?: string | null;
  reportedBy: string;
  title: string;
  findings: string;
  images: string[];
  status: ReportStatus;
  notes?: string | null;
  createdAt?: string;
  assignedUserId?: string | null;
  assignedUser?: { id: string; name: string; email: string; phone?: string | null } | null;
  properties?: { id: string; name: string; coverImage?: string | null; address?: string | null; city?: string | null };
  units?: { id: string; unitNumber: string };
  users?: { id: string; name: string; email: string };
  quotationIsFree?: boolean | null;
  quotationAmount?: string | number | null;
  quotationNotes?: string | null;
  quotationSubmittedAt?: string | null;
  quotationApprovedAt?: string | null;
}

interface UserListOption {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  roles?: { id: string; name: string } | null;
}

export const MaintenanceReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const canUpdateReports =
    hasPermission('update_maintenance_reports') || hasPermission('manage_maintenance_reports');
  const isMaintenanceTechnician = isMaintenanceRoleName(user?.role?.name);
  const [activeTab, setActiveTab] = useState<'general' | 'history'>('general');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateForm, setUpdateForm] = useState({ action: '', description: '' });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    findings: '',
    propertyId: '',
    unitId: '',
    status: 'pending' as ReportStatus,
    notes: '',
  });
  const [editImages, setEditImages] = useState<string[]>([]);
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [editPreviews, setEditPreviews] = useState<string[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [qIsFree, setQIsFree] = useState(true);
  const [qAmount, setQAmount] = useState('');
  const [qNotes, setQNotes] = useState('');
  const [logContent, setLogContent] = useState('');
  const [logFiles, setLogFiles] = useState<File[]>([]);
  const [logPreviews, setLogPreviews] = useState<string[]>([]);
  const [logError, setLogError] = useState<string | null>(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [finalComment, setFinalComment] = useState('');
  const [completeError, setCompleteError] = useState<string | null>(null);

  const {
    data: report,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['maintenance-report', id],
    queryFn: () => api.getMaintenanceReport(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: reportTimelineData, isLoading: reportTimelineLoading } = useQuery({
    queryKey: ['maintenance-report-timeline', id],
    queryFn: () => api.getMaintenanceReportTimeline(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!report || typeof report !== 'object') return;
    const d = report as MaintenanceReportDetailData;
    if (d.quotationApprovedAt) return;
    if (d.quotationSubmittedAt) {
      setQIsFree(d.quotationIsFree === true);
      setQAmount(
        d.quotationAmount != null && String(d.quotationAmount) !== '' ? String(d.quotationAmount) : '',
      );
      setQNotes(d.quotationNotes ?? '');
    } else {
      setQIsFree(true);
      setQAmount('');
      setQNotes('');
    }
  }, [report]);

  const { data: propertiesRes } = useQuery({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
    enabled: isEditModalOpen,
  });

  const { data: unitsData } = useQuery({
    queryKey: ['units', editForm.propertyId],
    queryFn: () => api.getUnits(editForm.propertyId),
    staleTime: 60_000,
    enabled: !!editForm.propertyId && isEditModalOpen,
  });

  const { data: usersForAssignRes } = useQuery({
    queryKey: ['users', 'assign-maintenance'],
    queryFn: () => api.getUsers({ limit: 500 }),
    staleTime: 60_000,
    enabled: canUpdateReports && !isMaintenanceTechnician && !!id && activeTab === 'general',
  });

  const assignableUsers = useMemo(() => {
    const list = (usersForAssignRes?.data ?? []) as UserListOption[];
    const mantenimiento = list.filter((u) =>
      (u.roles?.name ?? '').toLowerCase().includes('mantenimiento'),
    );
    return mantenimiento.length > 0 ? mantenimiento : list;
  }, [usersForAssignRes]);

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteMaintenanceReport(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      navigate('/maintenance');
    },
  });

  const addUpdateMutation = useMutation({
    mutationFn: async () => {
      let images: string[] = [];
      if (pendingFiles.length > 0) {
        const res = await api.uploadMaintenanceReportImages(pendingFiles);
        images = res.fileUrls ?? [];
      }
      return api.createMaintenanceReportUpdate(id!, {
        action: updateForm.action,
        description: updateForm.description,
        images,
      });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['maintenance-report-timeline', id] });
      await queryClient.refetchQueries({ queryKey: ['maintenance-report', id] });
      setIsUpdateModalOpen(false);
      setUpdateForm({ action: '', description: '' });
      pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
      setPendingFiles([]);
      setPendingPreviews([]);
      setUpdateError(null);
    },
    onError: (err: Error) => setUpdateError(err.message || 'Error al agregar actualización'),
  });

  const onFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: File[] = [];
    const previews: string[] = [];
    Array.from(files).forEach((f) => {
      if (!f.type.startsWith('image/')) return;
      next.push(f);
      previews.push(URL.createObjectURL(f));
    });
    setPendingFiles((prev) => [...prev, ...next]);
    setPendingPreviews((prev) => [...prev, ...previews]);
  };
  const onEditFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: File[] = [];
    const previews: string[] = [];
    Array.from(files).forEach((f) => {
      if (!f.type.startsWith('image/')) return;
      next.push(f);
      previews.push(URL.createObjectURL(f));
    });
    setEditFiles((prev) => [...prev, ...next]);
    setEditPreviews((prev) => [...prev, ...previews]);
  };

  const removeEditPending = (idx: number) => {
    const url = editPreviews[idx];
    if (url) URL.revokeObjectURL(url);
    setEditFiles((prev) => prev.filter((_, i) => i !== idx));
    setEditPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeExistingImage = (idx: number) => {
    setEditImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      let images = [...editImages];
      if (editFiles.length > 0) {
        const res = await api.uploadMaintenanceReportImages(editFiles);
        images = [...images, ...(res.fileUrls ?? [])];
      }
      return api.updateMaintenanceReport(id!, {
        propertyId: editForm.propertyId,
        unitId: editForm.unitId || null,
        title: editForm.title,
        findings: editForm.findings,
        status: editForm.status,
        notes: editForm.notes || null,
        images,
      });
    },
    onSuccess: (updated) => {
      if (updated && id) {
        queryClient.setQueryData(['maintenance-report', id], (old: unknown) => {
          const prev = old && typeof old === 'object' ? (old as Record<string, unknown>) : null;
          if (!prev) return updated;
          return { ...prev, ...updated } as typeof updated;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance-report-timeline', id] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setIsEditModalOpen(false);
      editPreviews.forEach((url) => URL.revokeObjectURL(url));
      setEditFiles([]);
      setEditPreviews([]);
      setToast({ message: 'Reporte actualizado correctamente.', type: 'success' });
      setEditError(null);
    },
    onError: (err: Error) => setEditError(err.message || 'Error al actualizar el reporte'),
  });

  const assignMutation = useMutation({
    mutationFn: (assignedUserId: string | null) =>
      api.updateMaintenanceReport(id!, { assignedUserId }),
    onSuccess: (updated) => {
      if (updated && id) {
        queryClient.setQueryData(['maintenance-report', id], (old: unknown) => {
          const prev = old && typeof old === 'object' ? (old as Record<string, unknown>) : null;
          if (!prev) return updated;
          return { ...prev, ...updated } as typeof updated;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-report-timeline', id] });
      setToast({ message: 'Asignación actualizada.', type: 'success' });
    },
    onError: (err: Error) =>
      setToast({ message: err.message || 'Error al actualizar la asignación', type: 'error' }),
  });

  const quotationMutation = useMutation({
    mutationFn: () => {
      const isFree = qIsFree;
      let amount: number | undefined;
      if (!isFree) {
        const raw = qAmount.replace(',', '.').trim();
        amount = parseFloat(raw);
        if (Number.isNaN(amount) || amount < 0) {
          return Promise.reject(new Error('Indica un monto válido en MXN o marca sin costo.'));
        }
      }
      return api.patchMaintenanceReportQuotation(id!, {
        isFree,
        amount,
        notes: qNotes.trim() || undefined,
      });
    },
    onSuccess: (updated) => {
      if (updated && id) {
        queryClient.setQueryData(['maintenance-report', id], (old: unknown) => {
          const prev = old && typeof old === 'object' ? (old as Record<string, unknown>) : null;
          if (!prev) return updated;
          return { ...prev, ...updated } as typeof updated;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance-report-timeline', id] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setToast({ message: 'Cotización guardada.', type: 'success' });
    },
    onError: (err: Error) =>
      setToast({ message: err.message || 'Error al guardar la cotización', type: 'error' }),
  });

  const approveQuotationMutation = useMutation({
    mutationFn: () => api.approveMaintenanceReportQuotation(id!),
    onSuccess: (updated) => {
      if (updated && id) {
        queryClient.setQueryData(['maintenance-report', id], (old: unknown) => {
          const prev = old && typeof old === 'object' ? (old as Record<string, unknown>) : null;
          if (!prev) return updated;
          return { ...prev, ...updated } as typeof updated;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance-report-timeline', id] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setToast({ message: 'Cotización aprobada. El trabajo queda en curso.', type: 'success' });
    },
    onError: (err: Error) =>
      setToast({ message: err.message || 'No se pudo aprobar la cotización', type: 'error' }),
  });

  const technicianLogMutation = useMutation({
    mutationFn: () => api.postMaintenanceTechnicianLog(id!, logContent, logFiles),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance-logs', id] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-report', id] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-report-timeline', id] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setLogContent('');
      setLogPreviews((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });
      setLogFiles([]);
      setLogError(null);
      setToast({ message: 'Avance registrado.', type: 'success' });
    },
    onError: (err: Error) => setLogError(err.message || 'Error al registrar el avance'),
  });

  const completeWorkMutation = useMutation({
    mutationFn: () => api.patchMaintenanceComplete(id!, finalComment.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance-logs', id] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-report', id] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-report-timeline', id] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setIsCompleteModalOpen(false);
      setFinalComment('');
      setCompleteError(null);
      setToast({ message: 'Trabajo finalizado. El inquilino será notificado.', type: 'success' });
    },
    onError: (err: Error) => setCompleteError(err.message || 'No se pudo finalizar el trabajo'),
  });

  const removePending = (idx: number) => {
    const url = pendingPreviews[idx];
    if (url) URL.revokeObjectURL(url);
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const onLogFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: File[] = [];
    const previews: string[] = [];
    Array.from(files).forEach((f) => {
      if (!f.type.startsWith('image/')) return;
      next.push(f);
      previews.push(URL.createObjectURL(f));
    });
    setLogFiles((prev) => [...prev, ...next]);
    setLogPreviews((prev) => [...prev, ...previews]);
  };

  const removeLogFile = (idx: number) => {
    const url = logPreviews[idx];
    if (url) URL.revokeObjectURL(url);
    setLogFiles((prev) => prev.filter((_, i) => i !== idx));
    setLogPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Cargando reporte...</p>
        </div>
      </div>
    );
  }

  const error =
    queryError != null ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;
  if (error || !report) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{error || 'Reporte no encontrado'}</p>
        <Button onClick={() => navigate('/maintenance')} className="mt-4">
          Volver al listado
        </Button>
      </div>
    );
  }

  const detail = report as MaintenanceReportDetailData;
  const propertyName = detail.properties?.name ?? 'Propiedad';
  const unitLabel = detail.units?.unitNumber ? `Unidad ${detail.units.unitNumber}` : '';
  const status: ReportStatus = detail.status || 'pending';
  const createdAtStr = detail.createdAt ? formatDateLong(detail.createdAt) : '—';

  const renderStatusBadge = (s: ReportStatus) => {
    const map: Record<ReportStatus, { label: string; className: string }> = {
      pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
      in_progress: { label: 'En curso', className: 'bg-sky-100 text-sky-800' },
      reviewed: { label: 'Revisado', className: 'bg-blue-100 text-blue-800' },
      resolved: { label: 'Resuelto', className: 'bg-green-100 text-green-800' },
    };
    const cfg = map[s] ?? map.pending;
    return (
      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${cfg.className}`}>
        {cfg.label}
      </span>
    );
  };

  const isAssignee = !!user?.id && detail.assignedUserId === user.id;
  /** Técnico asignado o personal con permisos de gestión (sin rol técnico puro). */
  const showBitacoraPanel =
    (isMaintenanceTechnician && isAssignee) || (canUpdateReports && !isMaintenanceTechnician);
  const canApproveQuote =
    canUpdateReports &&
    !isMaintenanceTechnician &&
    !!detail.quotationSubmittedAt &&
    !detail.quotationApprovedAt &&
    status !== 'resolved';
  const showTechQuotationForm =
    isMaintenanceTechnician &&
    isAssignee &&
    !detail.quotationApprovedAt &&
    status !== 'resolved';

  const quotationMoney = (v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === '') return '—';
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    if (Number.isNaN(n)) return '—';
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
  };

  const administrativeHistoryRows = useMemo<ChangeLogEntry[]>(() => {
    const events = Array.isArray(reportTimelineData) ? reportTimelineData : [];
    return events.map((ev: any, idx: number) => {
      const rawDescription = String(ev?.description ?? '').trim();
      const byMatch = rawDescription.match(/\s*·?\s*Por:\s*(.+)$/i);
      const modifiedBy = byMatch?.[1]?.trim() || 'Sistema';
      const cleanDescription = byMatch
        ? rawDescription.replace(/\s*·?\s*Por:\s*(.+)$/i, '').trim()
        : rawDescription;
      const typeLabel =
        String(ev?.type ?? '').toLowerCase().includes('quotation') ||
        String(ev?.type ?? '').toLowerCase().includes('status') ||
        String(ev?.type ?? '').toLowerCase().includes('assigned') ||
        String(ev?.type ?? '').toLowerCase().includes('admin')
          ? 'Mantenimiento'
          : 'Información';
      return {
        id: String(ev?.id ?? `timeline-${idx}`),
        entityId: detail.id,
        entityType: 'PROPERTY',
        type: typeLabel,
        description: String(ev?.title ?? 'Actualización'),
        oldValue: null,
        newValue: cleanDescription || '—',
        modifiedBy,
        createdAt: String(ev?.date ?? new Date().toISOString()),
      };
    });
  }, [reportTimelineData, detail?.id]);

  return (
    <div className="space-y-6">
      {/* Header (igual que PropertyDetail) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => {
              const path = location.pathname.startsWith('/property-management')
                ? '/property-management'
                : '/maintenance';
              navigate(path);
            }}
          >
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{propertyName}</h1>
        </div>
        {canUpdateReports && !isMaintenanceTechnician && (
          <Button
            variant="secondary"
            icon={Edit}
            onClick={() => {
              setEditError(null);
              setEditForm({
                title: detail.title,
                findings: detail.findings,
                propertyId: detail.propertyId,
                unitId: detail.unitId || '',
                status,
                notes: detail.notes || '',
              });
              setEditImages(detail.images ?? []);
              setEditFiles([]);
              setEditPreviews([]);
              setIsEditModalOpen(true);
            }}
          >
            Editar
          </Button>
        )}
      </div>

      {/* Tabs estilo PropertyDetail (General / Historial) */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {(['general', 'history'] as const).map((tabId) => (
            <button
              key={tabId}
              onClick={() => setActiveTab(tabId)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tabId
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tabId === 'general' ? 'General' : 'Historial'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Imagen de Portada / Fotos del reporte */}
          {detail.images && detail.images.length > 0 && (
            <Card title="Imagen de Portada">
              <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={getUploadsPublicUrl(detail.images[0])}
                  alt={detail.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {detail.images.length > 1 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                  {detail.images.slice(1).map((url, i) => (
                    <a key={i} href={getUploadsPublicUrl(url)} target="_blank" rel="noopener noreferrer">
                      <img
                        src={getUploadsPublicUrl(url)}
                        alt=""
                        className="w-full h-20 object-cover rounded-lg border border-gray-200 hover:opacity-90"
                      />
                    </a>
                  ))}
                </div>
              )}
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Información del Reporte">
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Propiedad</label>
                  <p className="text-base font-medium">{propertyName}</p>
                  {detail.properties?.address && (
                    <p className="text-sm text-gray-500">{detail.properties.address}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-600">Unidad</label>
                  <p className="text-base font-medium">{unitLabel || '—'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Motivo</label>
                  <p className="text-base font-medium">{detail.title}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Descripción del problema</label>
                  <p className="text-base text-gray-700 whitespace-pre-wrap">{detail.findings}</p>
                </div>
                {detail.notes && (
                  <div>
                    <label className="text-sm text-gray-600">Notas adicionales</label>
                    <p className="text-base text-gray-700 whitespace-pre-wrap">{detail.notes}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-600">Estado</label>
                  <div className="mt-1">{renderStatusBadge(status)}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Fecha del reporte</label>
                  <p className="text-base font-medium">{createdAtStr}</p>
                </div>
                {detail.users?.name && (
                  <div>
                    <label className="text-sm text-gray-600">Reportado por</label>
                    <p className="text-base font-medium">{detail.users.name}</p>
                  </div>
                )}
              </div>
            </Card>

            <Card title="Fotografías">
              {detail.images && detail.images.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {detail.images.map((url, i) => (
                    <a key={i} href={getUploadsPublicUrl(url)} target="_blank" rel="noopener noreferrer">
                      <img
                        src={getUploadsPublicUrl(url)}
                        alt=""
                        className="w-full h-24 object-cover rounded-lg border border-gray-200 hover:opacity-90"
                      />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Sin fotografías adjuntas</p>
              )}
            </Card>
          </div>

          {canUpdateReports && !isMaintenanceTechnician && (
            <Card title="Asignación">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asignar a</label>
                  <select
                    value={detail.assignedUser?.id ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      assignMutation.mutate(v === '' ? null : v);
                    }}
                    disabled={assignMutation.isPending}
                    className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-60"
                  >
                    <option value="">Sin asignar</option>
                    {assignableUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </select>
                </div>
                {detail.assignedUser ? (
                  <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 text-sm space-y-1">
                    <p>
                      <span className="text-gray-500">Nombre: </span>
                      <span className="font-medium text-gray-900">{detail.assignedUser.name}</span>
                    </p>
                    <p>
                      <span className="text-gray-500">Correo: </span>
                      <span className="text-gray-900">{detail.assignedUser.email}</span>
                    </p>
                    {detail.assignedUser.phone ? (
                      <p>
                        <span className="text-gray-500">Teléfono: </span>
                        <span className="text-gray-900">{detail.assignedUser.phone}</span>
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Sin asignar</p>
                )}
              </div>
            </Card>
          )}

          <Card title="Cotización">
            <div className="space-y-4 text-sm text-gray-700">
              {!detail.assignedUserId ? (
                <p className="text-gray-600">
                  Sin técnico asignado. Cuando administración asigne a alguien, podrá indicar si el servicio es{' '}
                  <strong>sin costo</strong> o el <strong>monto estimado</strong> para el inquilino.
                </p>
              ) : !detail.quotationSubmittedAt ? (
                <p className="text-gray-600">
                  El técnico asignado aún no registra la cotización. Verás aquí el monto o «sin costo» cuando la
                  envíe.
                </p>
              ) : (
                <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 space-y-2">
                  <p>
                    <span className="text-gray-500">Propuesta: </span>
                    <span className="font-medium text-gray-900">
                      {detail.quotationIsFree === true
                        ? 'Sin costo para el inquilino'
                        : quotationMoney(detail.quotationAmount)}
                    </span>
                  </p>
                  {detail.quotationNotes?.trim() ? (
                    <p className="whitespace-pre-wrap">
                      <span className="text-gray-500">Detalle: </span>
                      {detail.quotationNotes.trim()}
                    </p>
                  ) : null}
                  <p className="text-xs text-gray-500">
                    Registrada: {detail.quotationSubmittedAt ? formatDateTime(detail.quotationSubmittedAt) : '—'}
                  </p>
                  {detail.quotationApprovedAt ? (
                    <p className="text-emerald-800 font-medium">
                      Aprobada el {formatDateTime(detail.quotationApprovedAt)} — trabajo autorizado.
                    </p>
                  ) : (
                    <p className="text-amber-800 font-medium">Pendiente de aprobación de administración.</p>
                  )}
                </div>
              )}

              {showTechQuotationForm && (
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <p className="font-medium text-gray-900">Registrar o actualizar cotización</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={qIsFree}
                      onChange={(e) => setQIsFree(e.target.checked)}
                      disabled={quotationMutation.isPending}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span>Sin costo para el inquilino</span>
                  </label>
                  {!qIsFree && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Monto estimado (MXN)</label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={qAmount}
                        onChange={(e) => setQAmount(e.target.value)}
                        disabled={quotationMutation.isPending}
                        className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                    <textarea
                      value={qNotes}
                      onChange={(e) => setQNotes(e.target.value)}
                      rows={3}
                      disabled={quotationMutation.isPending}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Desglose, materiales, vigencia del precio…"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => quotationMutation.mutate()}
                    disabled={quotationMutation.isPending}
                  >
                    {quotationMutation.isPending ? 'Guardando…' : 'Guardar cotización'}
                  </Button>
                </div>
              )}

              {canApproveQuote && (
                <div className="border-t border-gray-200 pt-4">
                  <Button
                    type="button"
                    icon={CheckCircle2}
                    onClick={() => {
                      if (
                        !window.confirm(
                          '¿Aprobar esta cotización y pasar el reporte a «en curso»? El inquilino verá que el trabajo está autorizado.',
                        )
                      ) {
                        return;
                      }
                      approveQuotationMutation.mutate();
                    }}
                    disabled={approveQuotationMutation.isPending}
                  >
                    {approveQuotationMutation.isPending ? 'Aprobando…' : 'Aprobar cotización e iniciar trabajo'}
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {showBitacoraPanel && (
            <Card title="Bitácora de avances">
              {detail.id ? <MaintenanceTimeline reportId={detail.id} /> : null}
              {isMaintenanceTechnician && isAssignee && status !== 'resolved' && (
                <div className="mt-6 border-t border-gray-200 pt-6 space-y-4">
                  <p className="text-sm font-medium text-gray-900">Registrar avance</p>
                  {logError && (
                    <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">{logError}</div>
                  )}
                  <textarea
                    value={logContent}
                    onChange={(e) => setLogContent(e.target.value)}
                    rows={4}
                    disabled={technicianLogMutation.isPending}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Describe lo realizado, materiales usados o próximos pasos…"
                  />
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <ImageIcon className="w-4 h-4 text-gray-400" />
                        <span>Evidencia fotográfica (opcional)</span>
                      </div>
                      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm cursor-pointer hover:bg-gray-50">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          disabled={technicianLogMutation.isPending}
                          onChange={(e) => onLogFilesSelected(e.target.files)}
                        />
                        <Plus className="w-4 h-4" />
                        Añadir fotos
                      </label>
                    </div>
                    {logPreviews.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {logPreviews.map((url, i) => (
                          <div key={i} className="relative">
                            <img src={url} alt="" className="w-full h-20 object-cover rounded-lg border border-gray-200" />
                            <button
                              type="button"
                              onClick={() => removeLogFile(i)}
                              disabled={technicianLogMutation.isPending}
                              className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                              title="Quitar"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!logContent.trim()) {
                        setLogError('Escribe un comentario para el avance.');
                        return;
                      }
                      setLogError(null);
                      technicianLogMutation.mutate();
                    }}
                    disabled={technicianLogMutation.isPending}
                  >
                    {technicianLogMutation.isPending ? 'Enviando…' : 'Registrar avance'}
                  </Button>
                </div>
              )}
              {isMaintenanceTechnician && isAssignee && status === 'in_progress' && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    icon={CheckCircle2}
                    onClick={() => {
                      setCompleteError(null);
                      setFinalComment('');
                      setIsCompleteModalOpen(true);
                    }}
                  >
                    Finalizar trabajo
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          <MaintenanceReportWorkTimeline
            reportId={detail.id}
            status={status}
            assignedUserId={detail.assignedUserId}
            quotation={{
              isFree: detail.quotationIsFree,
              amount: detail.quotationAmount,
              notes: detail.quotationNotes,
              submittedAt: detail.quotationSubmittedAt,
              approvedAt: detail.quotationApprovedAt,
            }}
          />
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Historial administrativo</h2>
            {canUpdateReports && !isMaintenanceTechnician && (
              <Button
                size="sm"
                className="flex items-center gap-1"
                onClick={() => {
                  setUpdateError(null);
                  setIsUpdateModalOpen(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Agregar actualización
              </Button>
            )}
          </div>
          <Card>
            <HistoryTable
              rows={administrativeHistoryRows}
              loading={reportTimelineLoading}
              emptyMessage="No hay eventos administrativos registrados aún."
            />
          </Card>
        </div>
      )}

      {/* Modal agregar actualización */}
      <Modal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        title="Agregar actualización"
        size="lg"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!updateForm.action.trim() || !updateForm.description.trim()) {
              setUpdateError('Completa acción y descripción.');
              return;
            }
            addUpdateMutation.mutate();
          }}
          className="space-y-4"
        >
          {updateError && (
            <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">
              {updateError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acción *</label>
            <select
              value={updateForm.action}
              onChange={(e) => setUpdateForm((d) => ({ ...d, action: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              required
            >
              <option value="">Selecciona una acción</option>
              <option value="Visita realizada">Visita realizada</option>
              <option value="Trabajo en progreso">Trabajo en progreso</option>
              <option value="En espera de material">En espera de material</option>
              <option value="Completado">Completado</option>
              <option value="Resuelto">Resuelto</option>
              <option value="Requiere segunda visita">Requiere segunda visita</option>
              <option value="Otro">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
            <textarea
              value={updateForm.description}
              onChange={(e) => setUpdateForm((d) => ({ ...d, description: e.target.value }))}
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fotografías</label>
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <ImageIcon className="w-4 h-4 text-gray-400" />
                  <span>Sube múltiples imágenes (JPG/PNG)</span>
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => onFilesSelected(e.target.files)}
                  />
                  <Plus className="w-4 h-4" />
                  Añadir
                </label>
              </div>
              {pendingPreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {pendingPreviews.map((url, i) => (
                    <div key={i} className="relative">
                      <img src={url} alt="" className="w-full h-20 object-cover rounded-lg border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => removePending(i)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        title="Quitar"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsUpdateModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={addUpdateMutation.isPending}>
              {addUpdateMutation.isPending ? 'Guardando...' : 'Guardar actualización'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal editar reporte */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar reporte de mantenimiento"
        size="lg"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!editForm.propertyId || !editForm.title.trim() || !editForm.findings.trim()) {
              setEditError('Completa propiedad, título y descripción.');
              return;
            }
            editMutation.mutate();
          }}
          className="space-y-4"
        >
          {editError && (
            <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">
              {editError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título / Motivo *</label>
            <input
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm((d) => ({ ...d, title: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Ej. Fuga en baño"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción / Qué encontraste *</label>
            <textarea
              value={editForm.findings}
              onChange={(e) => setEditForm((d) => ({ ...d, findings: e.target.value }))}
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Propiedad *</label>
            <PropertySelect
              properties={(propertiesRes?.data ?? []) as Property[]}
              value={editForm.propertyId}
              onChange={(propertyId) => setEditForm((d) => ({ ...d, propertyId, unitId: '' }))}
              placeholder="Seleccione una propiedad"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
            <select
              value={editForm.unitId}
              onChange={(e) => setEditForm((d) => ({ ...d, unitId: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              disabled={!editForm.propertyId}
            >
              <option value="">(Opcional) Selecciona una unidad</option>
              {(Array.isArray(unitsData) ? unitsData : []).map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.unitNumber}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm((d) => ({ ...d, status: e.target.value as ReportStatus }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="pending">Pendiente</option>
                <option value="in_progress">En curso</option>
                <option value="reviewed">Revisado</option>
                <option value="resolved">Resuelto</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm((d) => ({ ...d, notes: e.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fotografías</label>
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <ImageIcon className="w-4 h-4 text-gray-400" />
                  <span>Agregar nuevas imágenes (JPG/PNG)</span>
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => onEditFilesSelected(e.target.files)}
                  />
                  <Plus className="w-4 h-4" />
                  Añadir
                </label>
              </div>
              {(editImages.length > 0 || editPreviews.length > 0) && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {editImages.map((url, i) => (
                    <div key={`existing-${i}`} className="relative">
                      <img
                        src={getUploadsPublicUrl(url)}
                        alt=""
                        className="w-full h-20 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(i)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        title="Quitar"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {editPreviews.map((url, i) => (
                    <div key={`pending-${i}`} className="relative">
                      <img
                        src={url}
                        alt=""
                        className="w-full h-20 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeEditPending(i)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        title="Quitar"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={editMutation.isPending}>
              {editMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isCompleteModalOpen}
        onClose={() => {
          if (!completeWorkMutation.isPending) {
            setIsCompleteModalOpen(false);
            setCompleteError(null);
          }
        }}
        title="Finalizar trabajo"
        size="md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!finalComment.trim()) {
              setCompleteError('El comentario final es obligatorio.');
              return;
            }
            setCompleteError(null);
            completeWorkMutation.mutate();
          }}
          className="space-y-4"
        >
          {completeError && (
            <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">{completeError}</div>
          )}
          <p className="text-sm text-gray-600">
            El reporte pasará a <strong>Resuelto</strong> y se notificará al inquilino.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comentario final *</label>
            <textarea
              value={finalComment}
              onChange={(e) => setFinalComment(e.target.value)}
              rows={4}
              disabled={completeWorkMutation.isPending}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Resumen del trabajo realizado…"
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (!completeWorkMutation.isPending) setIsCompleteModalOpen(false);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={completeWorkMutation.isPending}>
              {completeWorkMutation.isPending ? 'Finalizando…' : 'Confirmar cierre'}
            </Button>
          </div>
        </form>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

